/**
 * HUD parts composed from the design system: the attribution line (plain map text, no pill), the tools
 * island (Share this view · Full screen) with its share note, and the mobile
 * map buttons. Presentational apart from the share/fullscreen side effects.
 */
import { useCallback, useEffect, useRef, useState, type JSX, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { DUR, Icon, IconButton, cx, exitMs } from "@/design";
import { announce } from "@/store/bus";
import { getApp, useApp } from "@/store/store";
import { HUD_COPY } from "./copy";
import { openAbout } from "./aboutSignal";
import { shareViewUrl } from "./geometry";
import { fullscreenSupported, useFullscreen } from "./hooks";

/* ── attribution ────────────────────────────────────────────────────── */

/** §A.4: a tap on the mobile pill shows the full line for 6 s. */
export const EXPAND_MS = 6000;

export interface AttributionProps {
  /** Short line ("© OpenStreetMap · Protomaps · Mapterhorn"). The About sheet carries the full one. */
  compact: boolean;
  /** Mobile: a tap expands the full line for 6 s, with the way into the About sheet. */
  expandable: boolean;
  /** Carry "Powered by Flux Real" (not on mobile: the sheet's list carries it). */
  powered: boolean;
  kiosk?: boolean;
}

/** Keeps "©" with the name after it when the line wraps. */
const keepCopyright = (s: string): string => s.replace(/© /g, "©\u00a0");

function Powered(): JSX.Element {
  return (
    <span className="ld-powered ld-loc-hud-attr-powered">
      {HUD_COPY.poweredBy} <b>{HUD_COPY.poweredName}</b>
    </span>
  );
}

export function Attribution({ compact, expandable, powered, kiosk = false }: AttributionProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const mood = useApp((s) => s.mood); // plain text on the map: ink + halo follow the land's mood
  const hold = useRef(false);
  const timer = useRef(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const arm = useCallback(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      if (!hold.current) setOpen(false);
    }, EXPAND_MS);
  }, []);

  useEffect(() => {
    if (open) arm();
    return () => window.clearTimeout(timer.current);
  }, [open, arm]);

  // Leaving the mobile layout folds the line away.
  const [wasExpandable, setWasExpandable] = useState(expandable);
  if (wasExpandable !== expandable) {
    setWasExpandable(expandable);
    if (!expandable) setOpen(false);
  }

  if (!expandable) {
    // Desktop and kiosk: the line is the way into the About sheet (plain text on the map, no pill).
    const text = keepCopyright(compact ? HUD_COPY.attributionShort : HUD_COPY.attribution);
    return (
      <div className="ld-loc-hud-attr" data-kiosk={kiosk || undefined} data-mood={mood}>
        <button
          type="button"
          className="ld-loc-hud-attr-btn"
          aria-haspopup="dialog"
          aria-label={`${HUD_COPY.attribution}.${powered ? ` ${HUD_COPY.poweredBy} ${HUD_COPY.poweredName}.` : ""} ${HUD_COPY.about}`}
          onClick={(e) => openAbout(e.currentTarget)}
        >
          <Icon name="info" size={kiosk ? 15 : 13} className="ld-loc-hud-attr-icon" />
          <span className="ld-loc-hud-attr-text">{text}</span>
          {powered && (
            <>
              <span className="ld-loc-hud-attr-sep" aria-hidden="true" />
              <Powered />
            </>
          )}
        </button>
      </div>
    );
  }

  const release = () => {
    hold.current = false;
    if (open) arm();
  };
  const onKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape" && open) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      btnRef.current?.focus();
    }
  };
  return (
    <div
      ref={rootRef}
      className="ld-loc-hud-attr"
      data-mood={mood}
      data-open={open || undefined}
      data-expandable="true"
      onPointerEnter={() => (hold.current = true)}
      onPointerLeave={release}
      onFocus={() => (hold.current = true)}
      onBlur={(e) => {
        if (!rootRef.current?.contains(e.relatedTarget as Node | null)) release();
      }}
      onKeyDown={onKey}
    >
      <button
        ref={btnRef}
        type="button"
        className="ld-loc-hud-attr-btn"
        aria-expanded={open}
        aria-label={open ? undefined : `${HUD_COPY.attributionName}: ${HUD_COPY.attribution}`}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="info" size={13} className="ld-loc-hud-attr-icon" />
        <span key={open ? "full" : "short"} className="ld-loc-hud-attr-text ld-fade">
          {keepCopyright(open ? HUD_COPY.attribution : HUD_COPY.attributionShort)}
        </span>
      </button>
      {open && (
        <button type="button" className="ld-loc-hud-attr-about ld-fade" aria-haspopup="dialog" onClick={(e) => openAbout(e.currentTarget)}>
          {HUD_COPY.about}
          <Icon name="chevR" size={13} />
        </button>
      )}
    </div>
  );
}

/* ── tools island ───────────────────────────────────────────────────── */

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the selection copy */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    ta.style.pointerEvents = "none";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

type Note = { kind: "copied" } | { kind: "failed"; url: string } | null;
/** The note holds long enough to read, then leaves at the toast's exit (294 leave). */
const COPIED_MS = 2400;
const FAILED_MS = 15000;
const NOTE_OUT_MS = exitMs(DUR.calm);

export function ToolsIsland(): JSX.Element {
  const [fs, toggleFs] = useFullscreen();
  const [canFs] = useState(fullscreenSupported);
  const [note, setNote] = useState<Note>(null);
  const [leaving, setLeaving] = useState(false);
  const shareRef = useRef<HTMLDivElement>(null);
  const noteRef = useRef<HTMLDivElement>(null);
  const timer = useRef(0);
  const leaveTimer = useRef(0);

  const dismiss = useCallback((focusBack = false) => {
    window.clearTimeout(timer.current);
    setLeaving(true);
    window.clearTimeout(leaveTimer.current);
    leaveTimer.current = window.setTimeout(() => {
      setNote(null);
      setLeaving(false);
    }, NOTE_OUT_MS);
    if (focusBack) shareRef.current?.querySelector<HTMLElement>("button")?.focus();
  }, []);

  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
      window.clearTimeout(leaveTimer.current);
    },
    [],
  );

  // Failed: focus the link so it can be selected and copied by hand.
  useEffect(() => {
    if (note?.kind !== "failed") return;
    const input = noteRef.current?.querySelector("input");
    input?.focus();
    input?.select();
  }, [note]);

  // An outside press puts the note away.
  useEffect(() => {
    if (!note) return undefined;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (t && (noteRef.current?.contains(t) || shareRef.current?.contains(t))) return;
      dismiss();
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [note, dismiss]);

  const share = async () => {
    const url = shareViewUrl(window.location, getApp());
    const ok = await copyText(url);
    window.clearTimeout(timer.current);
    window.clearTimeout(leaveTimer.current);
    setLeaving(false);
    if (ok) {
      setNote({ kind: "copied" });
      announce(HUD_COPY.copiedAnnounce);
      timer.current = window.setTimeout(() => dismiss(), COPIED_MS);
    } else {
      setNote({ kind: "failed", url });
      announce(HUD_COPY.copyFailed);
      timer.current = window.setTimeout(() => dismiss(), FAILED_MS);
    }
  };

  const onNoteKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      dismiss(true);
    }
  };

  return (
    <div className="ld-loc-hud-tools">
      <div ref={shareRef} className="ld-island ld-glass ld-loc-hud-tools-island" role="group" aria-label={HUD_COPY.tools}>
        <IconButton icon="share" label={HUD_COPY.share} onClick={() => void share()} />
        {canFs && (
          <IconButton icon="expand" label={fs ? HUD_COPY.exitFullscreen : HUD_COPY.fullscreen} tip="left" className="ld-loc-hud-fs" data-on={fs || undefined} onClick={toggleFs} />
        )}
      </div>
      {note && (
        <div ref={noteRef} className={cx("ld-loc-hud-note ld-glass", leaving ? "ld-out" : "ld-in")} data-kind={note.kind} onKeyDown={onNoteKey}>
          {note.kind === "copied" ? (
            <span className="ld-loc-hud-note-row">
              <Icon name="check" size={16} className="ld-loc-hud-note-icon" />
              {HUD_COPY.copied}
            </span>
          ) : (
            <>
              <span className="ld-loc-hud-note-row">
                <span>{HUD_COPY.copyFailed}</span>
                <IconButton icon="close" label={HUD_COPY.dismiss} size={28} iconSize={14} tip={null} onClick={() => dismiss(true)} />
              </span>
              <input className="ld-loc-hud-note-input" readOnly value={note.url} aria-label={HUD_COPY.linkLabel} onFocus={(e) => e.currentTarget.select()} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── mobile map buttons ─────────────────────────────────────────────── */

export interface MapButtonsProps {
  onRecenter: () => void;
  onLayers: () => void;
  layersOpen: boolean;
  layersId: string;
  size: number;
  gap: number;
}

/** §A.4: "Back to the site" and "Layers" as solo glass buttons. No + and −: pinch zooms. Touch: no tooltips. */
export function MapButtons({ onRecenter, onLayers, layersOpen, layersId, size, gap }: MapButtonsProps): JSX.Element {
  return (
    <div className="ld-loc-hud-mapbtns" role="group" aria-label={HUD_COPY.mapView} style={{ gap }}>
      <IconButton icon="recenter" label={HUD_COPY.home} solo size={size} tip={null} onClick={onRecenter} />
      <IconButton
        icon="layers"
        label={HUD_COPY.layers}
        solo
        size={size}
        tip={null}
        aria-haspopup="dialog"
        aria-expanded={layersOpen}
        aria-controls={layersId}
        onClick={onLayers}
      />
    </div>
  );
}
